import { useState } from 'react';
import {
  Box,
  Button,
  FormControl,
  FormLabel,
  Heading,
  Input,
  Switch,
  Text,
  useColorModeValue,
  VStack,
  HStack,
  Icon,
  InputGroup,
  InputLeftElement,
  Container,
} from '@chakra-ui/react';
import { Link, Navigate } from 'react-router-dom';
import { useSignInEmailPassword } from '@nhost/react';
import { FiMail, FiLock, FiLogIn } from 'react-icons/fi';

function SignIn() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');

  const textColor = useColorModeValue('gray.700', 'white');
  const bgColor = useColorModeValue('white', 'gray.800');
  const brandColor = useColorModeValue('brand.500', 'brand.400');
  const inputBg = useColorModeValue('gray.50', 'gray.700');
  const borderColor = useColorModeValue('gray.200', 'gray.600');
  const iconColor = useColorModeValue('gray.500', 'gray.400');
  // const hoverBg = useColorModeValue('gray.50', 'gray.700');

  const { signInEmailPassword, isLoading, isSuccess, needsEmailVerification, isError, error } =
    useSignInEmailPassword();

  const handleOnSubmit = (e: { preventDefault: () => void }) => {
    e.preventDefault();
    try {
      signInEmailPassword(email, password);
    } catch (error) {
      console.log(error);
    }
  };

  if (isSuccess) {
    return <Navigate to="/" replace={true} />;
  }

  const disableForm = isLoading || needsEmailVerification;


  return (
    <Box minH="100vh" bg={bgColor}>
      {/* Hero Section with Gradient Overlay */}
      <Box
        bgImage="linear-gradient(to right, rgba(255, 26, 61, 0.85), rgba(255, 94, 0, 0.85)), url('/assets/img/food.jpg')"
        bgSize="cover"
        bgPosition="center"
        height="40vh"
        position="relative"
        boxShadow="xl"
        display="flex"
        alignItems="center"
        justifyContent="center"
      >
        <VStack spacing={4}>
          <Heading color="white" fontSize={{ base: "3xl", md: "4xl" }} fontWeight="bold">
            Welcome Back
          </Heading>
          <Text color="white" fontSize={{ base: "md", md: "lg" }} textAlign="center" maxW="md">
            Sign in to access your dashboard and manage your restaurant
          </Text>
        </VStack>
      </Box>

      {/* Sign In Form */}
      <Container maxW="md" py={12} zIndex={10} position="relative" top="-10">
        <VStack
          spacing={8}
          bg={bgColor}
          borderRadius="xl"
          boxShadow="xl"
          p={{ base: 6, md: 8 }}
          mt="-20"
          borderTop="4px solid"
          borderTopColor="brand.500"
        >
          <VStack spacing={2} w="full">
            <Heading size="lg" color={textColor} textAlign="center">
              Sign In
            </Heading>
            <Text color="gray.500" textAlign="center">
              Enter your credentials to continue
            </Text>
          </VStack>

          <FormControl>
            <FormLabel>Email</FormLabel>
            <InputGroup>
              <InputLeftElement pointerEvents="none">
                <Icon as={FiMail as any} color={iconColor} />
              </InputLeftElement>
              <Input
                type="email"
                placeholder="your.email@example.com"
                bg={inputBg}
                borderColor={borderColor}
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                _focus={{ borderColor: brandColor }}
              />
            </InputGroup>
          </FormControl>

          <FormControl>
            <FormLabel>Password</FormLabel>
            <InputGroup>
              <InputLeftElement pointerEvents="none">
                <Icon as={FiLock as any} color={iconColor} />
              </InputLeftElement>
              <Input
                type="password"
                placeholder="Enter your password"
                bg={inputBg}
                borderColor={borderColor}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                _focus={{ borderColor: brandColor }}
              />
            </InputGroup>
          </FormControl>

          {isError && (
            <Text color="red.500" fontSize="sm" w="full">
              {error?.message}
            </Text>
          )}

          <HStack justify="space-between" w="full">
            <FormControl display="flex" alignItems="center" w="auto">
              <Switch id="remember-login" colorScheme="brand" size="sm" />
              <FormLabel htmlFor="remember-login" mb="0" ms="2" fontSize="sm">
                Remember me
              </FormLabel>
            </FormControl>
            <Link to="/reset-password">
              <Text color={brandColor} fontSize="sm" fontWeight="medium">
                Forgot password?
              </Text>
            </Link>
          </HStack>

          <Button
            w="full"
            colorScheme="brand"
            size="lg"
            fontSize="md"
            onClick={handleOnSubmit}
            isLoading={isLoading}
            isDisabled={disableForm}
            leftIcon={FiLogIn as any}
          >
            Sign In
          </Button>

          {/* <VStack w="full" spacing={4}>
            <HStack w="full">
              <Divider borderColor={borderColor} />
              <Text fontSize="sm" color="gray.500" whiteSpace="nowrap" px={2}>
                or continue with
              </Text>
              <Divider borderColor={borderColor} />
            </HStack>

            <HStack spacing={4}>
              <Button
                variant="outline"
                leftIcon={FaGoogle as any}
                borderColor={borderColor}
                _hover={{ bg: hoverBg }}
                flex={1}
              >
                Google
              </Button>
              <Button
                variant="outline"
                leftIcon={FaFacebook as any}
                borderColor={borderColor}
                _hover={{ bg: hoverBg }}
                flex={1}
              >
                Facebook
              </Button>
            </HStack>
          </VStack> */}

          <Text color={textColor} fontSize="sm" textAlign="center">
            Don&apos;t have an account?
            <Link to="/sign-up">
              <Text as="span" color={brandColor} fontWeight="semibold">
                Sign Up
              </Text>
            </Link>
          </Text>

          <VStack spacing={2} w="full">
            <HStack spacing={4} justify="center">
              <Link to="/terms-and-conditions">
                <Text color="gray.500" fontSize="xs" _hover={{ color: brandColor }}>
                  Terms & Conditions
                </Text>
              </Link>
              <Text color="gray.400" fontSize="xs">•</Text>
              <Link to="/privacy-policy">
                <Text color="gray.500" fontSize="xs" _hover={{ color: brandColor }}>
                  Privacy Policy
                </Text>
              </Link>
            </HStack>
          </VStack>
        </VStack>
      </Container>
    </Box>
  );
}

export default SignIn;

// import { useState } from 'react';
// import { Link, Navigate } from 'react-router-dom';

// import { useSignInEmailPassword } from '@nhost/react';
// import Spinner from './Spinner';
// import { Input } from '@chakra-ui/react';

// const SignIn = () => {
// const [email, setEmail] = useState('');
// const [password, setPassword] = useState('');

// const { signInEmailPassword, isLoading, isSuccess, needsEmailVerification, isError, error } =
//   useSignInEmailPassword();

//   const handleOnSubmit = (e: { preventDefault: () => void }) => {
//     e.preventDefault();
//     signInEmailPassword(email, password);
//   };

//   if (isSuccess) {
//     return <Navigate to="/" replace={true} />;
//   }

//   const disableForm = isLoading || needsEmailVerification;

//   return (
//     <div className="w-full max-w-lg">
//       <div className="sm:rounded-xl sm:shadow-md sm:border border-opacity-50 sm:bg-white px-4 sm:px-8 py-12 flex flex-col items-center">
//         <div className="h-14">
//           <img src={process.env.PUBLIC_URL + 'logo.svg'} alt="logo" className="w-full h-full" />
//         </div>

//         {needsEmailVerification ? (
//           <p className="mt-12 text-center">
//             Please check your mailbox and follow the verification link to verify your email.
//           </p>
//         ) : (
//           <>
//             <form onSubmit={handleOnSubmit} className="w-full">
//               <div className="mt-12 w-full flex flex-col items-center space-y-6">
//                 <Input
//                   type="email"
//                   placeholder="Email address"
//                   value={email}
//                   onChange={(e) => setEmail(e.target.value)}
//                   disabled={disableForm}
//                   required
//                 />
//                 <Input
//                   type="password"
//                   placeholder="Password"
//                   value={password}
//                   onChange={(e) => setPassword(e.target.value)}
//                   disabled={disableForm}
//                   required
//                 />
//               </div>

//               <button
//                 type="submit"
//                 disabled={disableForm}
//                 className="mt-6 w-full font-medium inline-flex justify-center items-center rounded-md p-3 text-white bg-blue-600 hover:bg-blue-500 focus:outline-none focus:ring-4 focus:ring-blue-500 focus:ring-opacity-50 disabled:opacity-50 disabled:cursor-not-allowed  disabled:hover:bg-blue-600 disabled:hover:border-bg-600 transition-colors"
//               >
//                 {isLoading ? <Spinner size="sm" /> : 'Sign in'}
//               </button>

//               {isError ? <p className="mt-4 text-red-500 text-center">{error?.message}</p> : null}
//             </form>

//             <Link
//               to="/reset-password"
//               className="mt-4 text-blue-600 hover:text-blue-500 hover:underline hover:underline-offset-1 transition"
//             >
//               Forgot your password?
//             </Link>
//           </>
//         )}
//       </div>

//       <p className="sm:mt-8 text-gray-500 text-center">
//         No account yet?{' '}
//         <Link
//           to="/sign-up"
//           className="text-blue-600 hover:text-blue-500 hover:underline hover:underline-offset-1 transition"
//         >
//           Sign up
//         </Link>
//       </p>
//     </div>
//   );
// };

// export default SignIn;
